import React, { useState } from 'react'
import { isEmpty } from 'lodash'

import { getMeasuredCallsPhrase, getPlaybookName } from './AgenticInsights.helpers'

// How many names to show before collapsing the rest behind a toggle. A run's
// sample can touch a dozen playbooks and a hundred agents, and the panel is
// otherwise short.
const NAMES_SHOWN = 3

const NameList = ({ names }) => {
  const [expanded, setExpanded] = useState(false)
  const hidden = names.length - NAMES_SHOWN

  if (hidden <= 0) {
    return <span className="experiment-scope__names">{names.join(', ')}</span>
  }

  return (
    <span className="experiment-scope__names">
      {(expanded ? names : names.slice(0, NAMES_SHOWN)).join(', ')}
      <button
        type="button"
        className="experiment-scope__more"
        onClick={() => setExpanded((current) => !current)}
      >
        {expanded ? 'show fewer' : `+${hidden} more`}
      </button>
    </span>
  )
}

// Who an experiment reaches, and what gets measured. The three are not the same
// set, which is the whole reason this exists: guidance goes to playbooks,
// coaching goes to individual agents, and the measurement counts calls.
//
// Shown before launch as a preview and after launch as a record of where it went.
// After launch `coachedAgents` is who coaching really reached (read from the
// coaching records); before launch it's who it would reach.
export const ExperimentScope = ({
  insight,
  playbooks = [],
  playbookCids = [],
  coachedAgents = [],
  launched,
}) => {
  const playbookNames = playbookCids.map((cid) => getPlaybookName(playbooks, cid))
  // A run scoped to one playbook explains itself; a run that wasn't needs to say
  // why these playbooks and not others.
  const runIsPlaybookScoped = !isEmpty(insight.playbookCids)

  return (
    <div className="experiment-scope">
      <div className="experiment-scope__row">
        <span className="experiment-scope__label">Real-time guidance</span>
        {isEmpty(playbookNames) ? (
          <span className="experiment-scope__names">
            Not available — this run isn&apos;t tied to a playbook.
          </span>
        ) : (
          <>
            <NameList names={playbookNames} />
            <span className="experiment-scope__why">
              {runIsPlaybookScoped
                ? 'The playbook this run covered.'
                : 'The playbooks where calls for this conversion opportunity showed up.'}
            </span>
          </>
        )}
      </div>

      <div className="experiment-scope__row">
        <span className="experiment-scope__label">Post-call coaching</span>
        <span className="experiment-scope__count">
          {coachedAgents.length} {coachedAgents.length === 1 ? 'agent' : 'agents'}
        </span>
        {!isEmpty(coachedAgents) && <NameList names={coachedAgents} />}
        <span className="experiment-scope__why">
          {launched
            ? 'The agents this coaching went to.'
            : 'The agents who had calls where this came up.'}
        </span>
      </div>

      <div className="experiment-scope__row">
        <span className="experiment-scope__label">Measured</span>
        <span className="experiment-scope__names">{getMeasuredCallsPhrase(insight)}</span>
      </div>
    </div>
  )
}
